import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ServiceCompleted from "../../Modals/servicecompleted";
import { useLazyGetBookingDetailsByIdQuery } from "../../service/booking";
import { useEffect, useState } from "react";
import { showError } from "../../constants/toasts";
import Loader from "../../constants/Loader";
import dayjs from "dayjs";
import { PAYMENT_STATUS, BOOKING_STATUS, PAYMENT_METHOD } from "../../utils/enums";
import CurrencyIcon from "../../components/dirham";

const ViewServiceDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const fromOngoing = location.state?.fromOngoing;
  const [openModal, setOpenModal] = useState(false);
  const [bookingData, setBookingData] = useState<any>(null);

  const [getBookingDetailsById, { isLoading }] =
    useLazyGetBookingDetailsByIdQuery();
  const [selectedBooking, setSelectedBooking] = useState<any>("");

  const getBookingDetailsApi = async () => {
    try {
      const response = await getBookingDetailsById({ id }).unwrap();
      if (response.statusCode === 200) {
        setBookingData(response.data);
      }
    } catch (error: any) {
      showError(
        error?.data?.message ||
        "Something went wrong while fetching booking details",
      );
    }
  };

  useEffect(() => {
    if (id) {
      getBookingDetailsApi();
    }
  }, [id]);

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const getPaymentStatus = (status: any) => {
    switch (Number(status)) {
      case PAYMENT_STATUS.PAID:
        return "Paid";
      case PAYMENT_STATUS.PENDING:
        return "Pending";
      case PAYMENT_STATUS.FAILED:
        return "Failed";
      case PAYMENT_STATUS.REFUND_PROGRESS:
        return "Refund in Progress";
      case PAYMENT_STATUS.REFUNDED:
        return "Refunded";
      case PAYMENT_STATUS.NON_REFUNDABLE:
        return "Non-Refundable";
      default:
        return "N/A";
    }
  };

  const getStatusLabel = (status: any) => {
    switch (Number(status)) {
      case BOOKING_STATUS.PENDING:
        return "Pending";
      case BOOKING_STATUS.ACCEPTED:
        return "Accepted";
      case BOOKING_STATUS.ONGOING:
        return "Ongoing";
      case BOOKING_STATUS.COMPLETED:
        return "Completed";
      case BOOKING_STATUS.CANCELLED:
        return "Cancelled";
      case BOOKING_STATUS.REJECTED:
        return "Rejected";
      default:
        return "Unknown";
    }
  };

  const getPaymentMethod = (paymentMethod: any) => {
    switch (Number(paymentMethod)) {
      case PAYMENT_METHOD.CARD:
        return "Card";
      case PAYMENT_METHOD.UPI:
        return "UPI";
      case PAYMENT_METHOD.CASH:
        return "Cash";
      default:
        return "N/A";
    }
  };

  return (
    <>
      <Loader isLoad={isLoading} />
      <div className="view_order_page">
        <div className="main_title d_flex">
          <div className="left_s">
            <h1>View Service Booking</h1>
            <p>
              <button onClick={() => navigate("/dashboard")}>Dashboard</button>{" "}
              -{" "}
              <button onClick={() => navigate("/order-management")}>
                Service Management
              </button>{" "}
              - View
            </p>
          </div>
          {Number(bookingData?.status) === BOOKING_STATUS.ONGOING && (
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setSelectedBooking(id);
                  setOpenModal(true);
                }}
              >
                <DoneAllIcon />
                Mark as completed
              </Button>
            </div>
          )}
        </div>

        {/* Booking Summary */}
        <div className="cards view_page">
          <h3>Booking Details</h3>
          <div className="gap_p">
            <div className="w_33">
              <h6>Booking ID</h6>
              <p>{bookingData?.bookingId || "N/A"}</p>
            </div>
            <div className="w_33">
              <h6>Booking Date & Time</h6>
              <p>
                {bookingData?.date
                  ? dayjs(bookingData.date).format("YYYY-MM-DD HH:mm")
                  : "N/A"}
              </p>
            </div>

            <div className="w_33">
              <h6>Payment Method</h6>
              <p>{getPaymentMethod(bookingData?.paymentMethod)}</p>
            </div>

            <div className="w_33">
              <h6>Payment Status</h6>
              <p>{getPaymentStatus(bookingData?.paymentStatus)}</p>
            </div>

            <div className="w_33">
              <h6>Total Amount</h6>
              <p><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {bookingData?.totalAmount || "0.00"}</p>
            </div>

            <div className="w_33">
              <h6>Platform Fee</h6>
              <p><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {bookingData?.platformFee || "0.00"}</p>
            </div>

            <div className="w_33">
              <h6>Admin Commission</h6>
              <p><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {bookingData?.adminCommission || "0.00"}</p>
            </div>

            <div className="w_33">
              <h6>Net Payout</h6>
              <p><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {bookingData?.netPayout || "0.00"}</p>
            </div>

            <div className="w_33">
              <h6>Discount</h6>
              <p><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {bookingData?.discount || "0.00"}</p>
            </div>

            <div className="w_33">
              <h6>Tax (VAT)</h6>
              <p><CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {bookingData?.vatAmount || "0.00"}</p>
            </div>

            <div className="w_33">
              <h6>Status</h6>
              <p>{getStatusLabel(bookingData?.status)}</p>
            </div>
          </div>
        </div>

        {/* Vendor Details */}
        <div className="cards view_page">
          <h3>Vendor Details</h3>
          <div className="gap_p view_page">
            <div className="w_100 vew_img">
              <h6>Vendor Image</h6>
              <figure>
                <img
                  src={
                    bookingData?.vendor?.logo ||
                    bookingData?.vendor?.image ||
                    "/static/images/user_placeholder.png"
                  }
                  alt=""
                />
              </figure>
            </div>

            <div className="w_33">
              <h6>Vendor Name</h6>
              <p>{bookingData?.vendor?.storeName || "N/A"}</p>
            </div>

            <div className="w_33">
              <h6>Vendor Email</h6>
              <p>{bookingData?.vendor?.email || "N/A"}</p>
            </div>

            <div className="w_33">
              <h6>Vendor Phone</h6>
              <p>{bookingData?.vendor?.phone || "N/A"}</p>
            </div>
          </div>
        </div>

        {/* Service Details */}
        <div className="cards">
          <CardContent>
            <h3 style={{ marginBottom: "10px" }}>Service Details</h3>
            <Grid container spacing={2}>
              {bookingData?.service && (
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ display: "flex", padding: 1 }}>
                    <Box sx={{ width: 100, height: 100, mr: 2 }}>
                      <img
                        src={
                          bookingData?.service?.media?.[0]?.url ||
                          bookingData?.service?.image ||
                          "/static/images/placeholder.png"
                        }
                        alt={bookingData?.service?.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1">
                        {bookingData?.service?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        Category: {bookingData?.service?.category?.name || "N/A"}
                      </Typography>
                      <Typography variant="body2">
                        {bookingData?.service?.description || "No description"}
                      </Typography>
                      <Typography variant="body2">
                        Price: <CurrencyIcon width={12} height={12} style={{ verticalAlign: "baseline" }} /> {bookingData?.service?.price || "0.00"}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              )}
              {!bookingData?.service && (
                <Grid item xs={12}>
                  <Typography variant="body2">
                    No service details found.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </div>

        {/* Activity Log */}
        <div className="cards view_page">
          <h3 style={{ marginBottom: "10px" }}>Activity Log</h3>
          {bookingData?.activity?.length ? (
            <ul>
              {bookingData.activity.map((log: any, idx: number) => (
                <li key={idx}>
                  <strong>
                    {dayjs(log.createdAt).format("DD MMM YYYY, hh:mm A")}
                  </strong>
                  : {log.message} ({log.actionBy})
                </li>
              ))}
            </ul>
          ) : (
            <p>No activity recorded.</p>
          )}
        </div>

        {bookingData?.vendorReason && (
          <div className="cards view_page">
            <h3>Vendor rejected your submission</h3>
            <h3>Reason:-</h3>
            <p>{bookingData?.vendorReason}</p>
            <div className="mt_20">
              <Button
                onClick={() => {
                  setSelectedBooking(id);
                  setOpenModal(true);
                }}
                className="btn btn_primary"
              >
                Submit Again
              </Button>
            </div>
          </div>
        )}
      </div>
      <ServiceCompleted
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        selectedItem={selectedBooking}
        getBookingDetailsApi={getBookingDetailsApi}
      />
    </>
  );
};

export default ViewServiceDetails;
